import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";

import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { Col, ElementWrapper, Row } from "components/BaseComponents/Flex";
import { Button, Spinner, Typography } from "components";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import { TokenInclude } from "helpers/GetToken";

import {
  StyledBgWrapper,
  StyledTitleWrapper,
  StyledIconCircle,
  StyledItemWrapper,
  StyledContactDataWrapper,
} from "./ContactInfo.styles";
import Image, { ICONS } from "components/BaseComponents/Image";
import { ContactInfoAsync } from "services/employeeProfile.service";
import { setServiceError } from "redux/slices/ServiceInlineError";
import {
  ContactInfoType,
  IContactInfoResponse,
} from "models/Employee/employeeProfile.model";

const ContactInfo: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();

  const [loading, setLoading] = useState<boolean>(false);
  const [contactData, setContactData] = useState<IContactInfoResponse>();

  useEffect(() => {
    async function runContactInfo() {
      setLoading(true);
      const retVal = await ContactInfoAsync(token);
      if (retVal?.DialogBoxes?.length < 1) {
        setContactData(retVal);
        setLoading(false);
      } else {
        const errMessage: any = retVal;
        setLoading(false);
        dispatch(setServiceError(errMessage));
        return;
      }
    }
    runContactInfo();
  }, []);

  return (
    <ElementWrapper
      width='872px'
      height='calc(100vh + 369px)'
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='10px auto'
      padding={`${isMobile ? "0 5px !important" : ""}`}
    >
      <Spinner show={loading} />
      <StyledTitleWrapper>
        <Typography width='' size='16px' color={DIGIPOSTHEME.colors.white}>
          {t("contact_info")}
        </Typography>
      </StyledTitleWrapper>
      <StyledBgWrapper>
        <StyledItemWrapper>
          <Row padding='10px 0 0 0'>
            <Typography
              size={isMobile ? "16px" : "18px"}
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='300'
              transform='uppercase'
            >
              {t("addresses")}
            </Typography>
          </Row>

          <Row>
            <Col maxWidth='52px !important' margin='-20px 0 0 0'>
              <StyledIconCircle>
                <Image
                  alt='Menu Icon'
                  height='20px'
                  srcSet={ICONS.BUSINESS}
                  width='20px'
                />
              </StyledIconCircle>
            </Col>
            <Col margin={`${isMobile ? "0 10px !important" : ""}`}>
              {contactData?.AddressList.map(
                (adr: ContactInfoType, index: number) => (
                  <StyledContactDataWrapper key={index}>
                    <Row noMargin padding={`${isMobile ? "" : "0 0 0 20px"}`}>
                      <Typography
                        size={isMobile ? "15px" : "17px"}
                        color={DIGIPOSTHEME.colors.black}
                        weight='600'
                      >
                        {adr.Title}
                      </Typography>
                    </Row>
                    <Row>
                      <Typography
                        size={isMobile ? "14px" : "17px"}
                        color={DIGIPOSTHEME.colors.secondaryGraySecondary}
                        weight='300'
                        transform='uppercase'
                      >
                        {adr.Desc}
                      </Typography>
                    </Row>
                  </StyledContactDataWrapper>
                )
              )}
            </Col>
          </Row>
        </StyledItemWrapper>

        <StyledItemWrapper>
          <Row>
            <Typography
              size={isMobile ? "16px" : "18px"}
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='300'
              transform='uppercase'
            >
              {t("telephone_numbers")}
            </Typography>
          </Row>
          {contactData?.PhoneList.map((tlp: ContactInfoType, index: number) => (
            <StyledContactDataWrapper key={index}>
              <Row>
                <Col maxWidth='52px !important' margin='-20px 0 0 0'>
                  <StyledIconCircle>
                    <Image
                      alt='Menu Icon'
                      height='20px'
                      srcSet={
                        tlp.Type === 3 ? ICONS.MOBILEPHONE : ICONS.TELEPHONE
                      }
                      width='20px'
                    />
                  </StyledIconCircle>
                </Col>
                <Col margin={`${isMobile ? "0 10px !important" : ""}`}>
                  <Row noMargin padding={`${isMobile ? "" : "0 0 0 20px"}`}>
                    <Typography
                      size={isMobile ? "15px" : "17px"}
                      color={DIGIPOSTHEME.colors.black}
                      weight='600'
                    >
                      {tlp.Title}
                    </Typography>
                  </Row>
                  <Row>
                    <Typography
                      size={isMobile ? "14px" : "17px"}
                      color={DIGIPOSTHEME.colors.secondaryGraySecondary}
                      weight='300'
                    >
                      {tlp.Desc}
                    </Typography>
                  </Row>
                </Col>
              </Row>
            </StyledContactDataWrapper>
          ))}
        </StyledItemWrapper>

        <StyledItemWrapper>
          <Row>
            <Typography
              size={isMobile ? "16px" : "18px"}
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='300'
              transform='uppercase'
            >
              {t("email_numbers")}
            </Typography>
          </Row>

          <Row>
            <Col maxWidth='52px !important' margin='-20px 0 0 0'>
              <StyledIconCircle>
                <Image
                  alt='Menu Icon'
                  height='20px'
                  srcSet={ICONS.EMAIL}
                  width='20px'
                />
              </StyledIconCircle>
            </Col>
            <Col margin={`${isMobile ? "0 10px !important" : ""}`}>
              {contactData?.EmailList.map(
                (e: ContactInfoType, index: number) => (
                  <StyledContactDataWrapper key={index}>
                    <Row noMargin padding={`${isMobile ? "" : "0 0 0 20px"}`}>
                      <Typography
                        size={isMobile ? "15px" : "17px"}
                        color={DIGIPOSTHEME.colors.black}
                        weight='600'
                      >
                        {e.Title}
                      </Typography>
                    </Row>
                    <Row>
                      <Typography
                        size={isMobile ? "14px" : "17px"}
                        color={DIGIPOSTHEME.colors.secondaryGraySecondary}
                        weight='300'
                        transform='uppercase'
                      >
                        {e.Desc}
                      </Typography>
                    </Row>
                  </StyledContactDataWrapper>
                )
              )}
            </Col>
          </Row>
        </StyledItemWrapper>
      </StyledBgWrapper>
      <Row
        backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
        justifyContent='space-between'
        isResponsive
        noMargin
      >
        <Col maxWidth='130px'>
          <Button
            type='button'
            width={isMobile ? "100%" : "100px"}
            text={`← ${t("back")}`}
            onClick={() => navigate(-1)}
            color='light'
          />
        </Col>
      </Row>
    </ElementWrapper>
  );
};

export default ContactInfo;
